<template>
  <div class="kvass-media-render-vimeo">
    <div
      v-show="ready"
      ref="vimeoPlayer"
      class="kvass-media-render-vimeo__embed"
      frameborder="0"
      allow="autoplay; fullscreen;"
      webkitallowfullscreen
      mozallowfullscreen
    />

    <transition name="fade" mode="in-out">
      <div
        v-show="!ready && thumbnail"
        class="kvass-media-render-vimeo__thumbnail"
        :style="{ backgroundImage: `url(${thumbnail})` }"
      />
    </transition>
    <div v-if="showConsent" class="kvass-media-render-vimeo__blocked">
      <div
        class="kvass-media-render-vimeo__blocked-warning"
        :class="'kvass-media-render-vimeo__blocked-warning--' + mode"
      >
        {{ consentOptions.consentBlockMessage }}
        <ButtonComponent
          :label="consentOptions.consentButtonLabel"
          @click="openConsent"
          type="button"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { ButtonComponent } from 'vue-elder-button'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { WaitUntil, LoadScript, ExtractString } from '../utils'
import { Options } from '../index'
import './icons.js'

const eventsToEmit = ['play', 'playing', 'pause', 'ended']

const modeOptions = {
  slider: {
    autoplay: 1,
    controls: 0,
    autopause: 0,
  },
}

export default {
  props: {
    value: {
      type: Object,
      default: () => ({}),
    },
    options: {
      type: Object,
      default: () => ({}),
    },
    loop: {
      type: Boolean,
      default: false,
    },
    autoplay: {
      type: Boolean,
      default: false,
    },
    duration: {
      type: Number,
      default: 4000,
    },
    controls: {
      type: Boolean,
      default: true,
    },
    mute: {
      type: Boolean,
      default: false,
    },
    displayThumbnail: {
      type: Boolean,
      default: true,
    },
    mode: {
      enum: ['slider'],
      type: String,
      default: 'default',
    },
    ignoreConsent: {
      type: Boolean,
      default: false,
    },
    hideConsent: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      ready: false,
      player: null,
      thumbnail: null,
      consents: [],
    }
  },
  computed: {
    showConsent() {
      if (this.hideConsent) return false
      return !this.ignoreConsent && !this.consents.includes('statistics')
    },
    consentOptions() {
      return Options
    },
    videoId() {
      if (!this.value.url) return
      let value = this.value.url
      return [
        ExtractString(value, /https\:\/\/(w{3}\.)?vimeo\.com\/(.+)\/?/, {
          group: 2,
        }),
        ExtractString(value, /https\:\/\/(w{3}\.)?player\.vimeo\.com\/(video\/)?(.+)\/?/, {
          group: 3,
        }),
      ]
        .find(e => Boolean(e))
        .split('&')[0]
    },
  },

  methods: {
    openConsent() {
      Kvass.emit('consent:show')
    },
    play() {
      if (!this.player || !this.ready) return
      this.player.play()
    },
    playOnFocus() {
      if (window.innerWidth >= 992 || this.mode !== 'slider') return
      this.play()
    },

    async init() {
      const mergeOptions = {
        id: this.videoId,
        loop: this.loop,
        autoplay: this.autoplay,
        controls: this.controls,
        muted: this.mute || this.mode === 'slider' || this.autoplay,
        playsinline: 1,
        dnt: 1,
        ...(this.mode ? modeOptions[this.mode] : {}),
      }

      Object.assign(mergeOptions, this.options)

      LoadScript('https://player.vimeo.com/api/player.js')
      const Vimeo = await WaitUntil(() => window.Vimeo, { limit: 100 })
      this.player = new Vimeo.Player(this.$refs.vimeoPlayer, {
        ...mergeOptions,
      })
      await this.setEvents()
    },
    setEvents() {
      this.player
        .ready()
        .then(() => {
          this.ready = true
        })
        .catch(error => {
          console.log(error)
        })
      eventsToEmit.forEach(event => this.emitEvent(event))
      //for small device - play on focus
      window.addEventListener('focus', this.playOnFocus)
    },

    emitEvent(event) {
      this.player.on(event, () => {
        //only emit events if the slider is using the component
        if (!this.mode === 'slider') return
        this.$nextTick(() => this.$emit(event))
      })
    },
  },

  async mounted() {
    if (this.displayThumbnail) this.thumbnail = `/api/media/thumbnail?url=${this.value.url}`

    if (this.ignoreConsent) return this.init()

    let onChange = consents => {
      this.consents = consents || []
      if (!this.consents || !this.consents.length) return
      if (this.consents.includes('statistics')) return this.init()

      setTimeout(() => this.$emit('ended'), this.duration)
    }

    Kvass.emit('consent:get', c => onChange(c))
    Kvass.on('consent:change', onChange)
  },
  beforeDestroy() {
    window.removeEventListener('focus', this.playOnFocus)
  },
  components: {
    FontAwesomeIcon,
    ButtonComponent,
  },
}
</script>

<style lang="scss">
.kvass-media-render-vimeo {
  background-color: hsla(var(--primary-h), var(--primary-s), var(--primary-l), 0.07);

  iframe {
    height: 100%;
    width: 100%;
  }

  & > * {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }

  &__thumbnail {
    background-size: cover;
    background-repeat: no-repeat;
    background-position: center;
    filter: blur(10px);
    opacity: 0.7;
  }

  &__blocked {
    display: flex;
    justify-content: center;
    align-items: center;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    svg {
      opacity: 0.3;
    }

    @media (max-width: 992px) {
      svg {
        font-size: 2.5em;
      }
    }
    &-warning {
      pointer-events: initial;
      position: absolute;
      bottom: 0;
      text-align: center;
      background-color: #f2f2f2;
      font-size: 0.6em;
      bottom: 3rem;
      left: 3rem;
      max-width: 400px;
      font-size: 0.8em;
      padding: 1.5em 2em;

      .elder-button {
        margin-top: 1rem;
      }

      @media (max-width: 992px) {
        left: 0.5rem;
        bottom: 0.5rem;
        font-size: 0.6em;
        max-width: 200px;
      }
    }
  }
  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 500ms;
  }
  .fade-enter,
  .fade-leave-to {
    opacity: 0;
  }
}
</style>
